import {
  DurationFilterOptions,
  ID,
  SortingMode,
  Task,
  TaskListTreeItem,
  Timestamp,
  WeekDays,
} from '@griply/types';
import {
  buildUpcomingDeadlineBuckets,
  buildUpcomingDeadlineTree,
} from '@griply/utils';
import { useMemo } from 'react';

export {
  upcomingMonthPrefix,
  upcomingRestOfMonthPrefix,
  upcomingYearPrefix,
} from '@griply/types';

export type UpcomingDeadlineTreeOptions = {
  tasks: Task[];
  customOrder?: Record<ID, ID[]>;
  sortingMode?: SortingMode | null;
  today: Timestamp;
  weekStartsOn: WeekDays;
  dateFilterEndDate?: Timestamp | null;
  hideNewTaskRow?: boolean;
  shouldHaveRestOfMonth: boolean;
  restOfMonthStartDate: Timestamp;
  showCompleted?: boolean;
  durationFilters?: DurationFilterOptions[];
};

export const useUpcomingDeadlineTree = ({
  tasks,
  customOrder,
  sortingMode,
  today,
  weekStartsOn,
  dateFilterEndDate,
  hideNewTaskRow,
  shouldHaveRestOfMonth,
  restOfMonthStartDate,
  showCompleted,
  durationFilters,
}: UpcomingDeadlineTreeOptions): Record<ID, TaskListTreeItem> => {
  // Bucketing is the expensive per-task occurrence expansion (13-month
  // window). Memo deps exclude customOrder/sortingMode so reorders don't
  // re-expand schedules. See upcoming.invariants.md §5.
  const buckets = useMemo(
    () =>
      buildUpcomingDeadlineBuckets({
        tasks,
        today,
        weekStartsOn,
        dateFilterEndDate,
        shouldHaveRestOfMonth,
        restOfMonthStartDate,
        showCompleted,
        durationFilters,
      }),
    [
      tasks,
      today,
      weekStartsOn,
      dateFilterEndDate,
      shouldHaveRestOfMonth,
      restOfMonthStartDate,
      showCompleted,
      durationFilters,
    ],
  );

  return useMemo(
    () =>
      buildUpcomingDeadlineTree({
        buckets,
        customOrder,
        sortingMode,
        today,
        weekStartsOn,
        hideNewTaskRow,
        rootLabel: 'Root',
        newTaskLabel: 'New Task',
      }),
    [buckets, sortingMode, customOrder, hideNewTaskRow, today, weekStartsOn],
  );
};
